import { useRef, useState } from "react";
import { usePageTitle } from "@/i18n/LanguageContext";
import { MEDIA, photoSources } from "../media";
import { pages } from "../content/pages";
import { useContent, SlotImg, Rich } from "../ui/helpers";
import { PageShell, PageHero, CtaSignOff, StatusChip, useInViewClass } from "../ui/blocks";

const ORDER_TOTAL = 500;
const START = 240;
const STEP = 24;

/* Click-to-try production record (sample data, nothing is saved) */
function Ticket({ t }) {
  const [made, setMade] = useState(START);
  const [n, setN] = useState(24);
  const [hint, setHint] = useState(t.hint);
  const [bump, setBump] = useState(false);
  const pct = Math.round((made / ORDER_TOTAL) * 100);
  const record = () => {
    if (made >= ORDER_TOTAL) { setHint(t.full); return; }
    const next = Math.min(ORDER_TOTAL, made + STEP);
    setMade(next); setN((x) => x + 1);
    setHint(next >= ORDER_TOTAL ? t.done : t.updated);
    setBump(true); setTimeout(() => setBump(false), 350);
  };
  const reset = () => { setMade(START); setN(24); setHint(t.hint); };
  return (
    <div className="ticket rv" aria-live="polite">
      <div className="ticket-top"><span>{t.top}</span><span>{t.sample}</span></div>
      <div className={`ticket-rec${bump ? " bump" : ""}`}><small>{t.record}</small><strong>{`${t.bundle} B-${String(n).padStart(3, "0")}`}</strong><span>{t.sub}</span></div>
      <p className="ticket-meta">{t.meta}</p>
      <div className="ticket-big"><b>{made}</b><span>{t.of}</span></div>
      <div className="bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={t.completion}><i style={{ "--w": `${pct}%` }} /></div>
      <div className="bar-row"><span>{t.completion}</span><span>{pct}%</span></div>
      <button className="k-btn k-btn-mint" type="button" onClick={record}>{t.button}</button>
      <div className="ticket-foot"><span aria-live="polite">{hint}</span><button type="button" onClick={reset}>{t.reset}</button></div>
    </div>
  );
}

/* Engagement rows with a photo that follows the cursor (desktop) */
function Areas({ c }) {
  const [peek, setPeek] = useState({ on: false, x: 0, y: 0, src: "" });
  return (
    <section className="navy sec" id="areas">
      <div className="k-wrap">
        <div className="sec-head rv"><h2 className="h-sec">{c.title}</h2><p>{c.intro}</p></div>
        <ol className="k-rows">
          {c.items.map((a, i) => (
            <li key={a.title} className="erow"
              onMouseEnter={() => setPeek((p) => ({ ...p, on: true, src: photoSources(MEDIA[a.media]).src }))}
              onMouseLeave={() => setPeek((p) => ({ ...p, on: false }))}
              onMouseMove={(e) => setPeek((p) => ({ ...p, x: e.clientX + 170, y: e.clientY }))}>
              <span className="n">{String(i + 1).padStart(2, "0")}</span>
              <h3>{a.title}</h3>
              <div>
                <p><Rich text={a.body} /></p>
                <ul>{a.chips.map((x) => <li key={x}>{x}</li>)}</ul>
                <SlotImg slot={a.media} className="erow-img" decorative sizes="100vw" />
              </div>
              <StatusChip status={a.status} />
            </li>
          ))}
        </ol>
      </div>
      <div className={`peek${peek.on ? " on" : ""}`} style={{ left: peek.x, top: peek.y }} aria-hidden="true">
        {peek.src && <img src={peek.src} alt="" />}
      </div>
    </section>
  );
}

function PartnerDiagram({ d }) {
  return (
    <svg viewBox="0 0 560 380" role="img" aria-label={`${d.a} + ${d.b} → KSS → ${d.c1} ${d.c2}`}>
      <g fontFamily="IBM Plex Mono, Noto Sans KR, monospace" fontSize="11" letterSpacing="1.2">
        <rect x="16" y="64" width="170" height="64" rx="12" fill="#0B2434" stroke="#16E6B2" strokeOpacity=".5" />
        <text x="34" y="92" fill="#16E6B2">01</text><text x="34" y="112" fill="#E8EFF2">{d.a}</text>
        <rect x="16" y="252" width="170" height="64" rx="12" fill="#0B2434" stroke="#1EC8FF" strokeOpacity=".5" />
        <text x="34" y="280" fill="#1EC8FF">02</text><text x="34" y="300" fill="#E8EFF2">{d.b}</text>
        <rect x="404" y="158" width="140" height="64" rx="12" fill="#16E6B2" />
        <text x="474" y="186" textAnchor="middle" fill="#04211A">{d.c1}</text><text x="474" y="204" textAnchor="middle" fill="#04211A">{d.c2}</text>
      </g>
      <g fill="none" strokeWidth="2" className="flowline">
        <path d="M186 96 C 240 96, 230 190, 238 190" stroke="#16E6B2" /><path d="M186 284 C 240 284, 230 190, 238 190" stroke="#1EC8FF" /><path d="M322 190 L 404 190" stroke="#16E6B2" />
      </g>
      <circle cx="280" cy="190" r="70" fill="none" stroke="#9DB2BD" strokeOpacity=".25" strokeDasharray="3 6" className="orbit" />
      <circle cx="280" cy="190" r="42" fill="#0B2434" stroke="#E8EFF2" strokeOpacity=".6" />
      <text x="280" y="196" textAnchor="middle" fontFamily="Archivo, sans-serif" fontWeight="700" fontSize="18" fill="#fff">KSS</text>
    </svg>
  );
}

export default function ProjectsPage() {
  usePageTitle("projects");
  const c = useContent(pages).projects;
  const checks = useRef(null);
  useInViewClass(checks, 0.3);

  return (
    <PageShell testId="projects-page">
      <PageHero hero={c.hero} />

      <section className="band-light sec" id="pilot">
        <div className="k-wrap pilot">
          <div className="rv">
            <p className="k-label">{c.pilot.label}</p>
            <h2 className="h-sec">{c.pilot.title}</h2>
            <dl className="facts">{c.pilot.facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd><Rich text={v} /></dd></div>)}</dl>
          </div>
          <Ticket t={c.pilot.ticket} />
        </div>
      </section>

      <Areas c={c.areas} />

      <section className="mint-2 sec" id="partners">
        <div className="k-wrap">
          <div className="sec-head rv">
            <div>
              <p className="k-label">{c.partners.eyebrow}</p>
              <h2 className="h-sec" style={{ marginTop: 16 }}>{c.partners.title}</h2>
            </div>
          </div>
        </div>
        <div className="k-wrap partner">
          <div className="rv">
            <div className="roles2">{c.partners.roles.map(([b, s, p]) => <div key={b}><b>{b}</b><strong>{s}</strong><p>{p}</p></div>)}</div>
          </div>
          <div className="pdiag rv"><PartnerDiagram d={c.partners.diagram} /></div>
        </div>
      </section>

      <section className="band-light sec" id="principles">
        <div className="k-wrap">
          <div className="sec-head rv"><h2 className="h-sec">{c.principles.title}</h2><p>{c.principles.intro}</p></div>
          <ol className="checks" ref={checks}>
            {c.principles.items.map(([t, b], i) => <li key={t} style={{ "--d": `${i * 0.15}s` }}><i /><div><h3>{t}</h3><p>{b}</p></div></li>)}
          </ol>
        </div>
      </section>

      <CtaSignOff cta={c.cta} />
    </PageShell>
  );
}
